'use client';

import { type RefObject, useEffect } from 'react';

interface FaqAnchorConfig {
  cmsContentId?: string;
  tabInputId?: string;
}

const FAQ_ANCHOR_CONFIG: Record<string, FaqAnchorConfig> = {
  'perguntas-frequentes': {},
  cadastro: {
    cmsContentId: 'cadastro',
    tabInputId: 'tab-cadastro'
  },
  pagamentos: {
    cmsContentId: 'pagamentos',
    tabInputId: 'tab-pagamentos'
  },
  'trocas-e-devolucoes': {
    cmsContentId: 'devolucoes',
    tabInputId: 'tab-trocas'
  },
  entregas: {
    cmsContentId: 'entregas',
    tabInputId: 'tab-entregas'
  },
  pedidos: {
    cmsContentId: 'pedidos',
    tabInputId: 'tab-pedidos'
  },
  cupons: {
    cmsContentId: 'cupons',
    tabInputId: 'tab-cupons'
  },
  'validade-e-armazenamento': {
    cmsContentId: 'validade',
    tabInputId: 'tab-validade'
  },
  'modo-de-consumo': {
    cmsContentId: 'consumo',
    tabInputId: 'tab-consumo'
  }
};

const FAQ_ANCHOR_IDS = new Set(Object.keys(FAQ_ANCHOR_CONFIG));

const FAQ_HASH_ALIASES: Record<string, string> = {
  consumo: 'modo-de-consumo',
  devolucoes: 'trocas-e-devolucoes',
  'politica-de-entrega': 'entregas',
  trocas: 'trocas-e-devolucoes',
  validade: 'validade-e-armazenamento'
};

const normalizeText = (text: string) =>
  text.replace(/\s+/g, ' ').trim().toLowerCase();

export const getFaqAnchorId = (text: string) =>
  normalizeText(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getLastHashSegment = (anchor: string) =>
  anchor
    .replace(/^#+/, '')
    .split('#')
    .filter(Boolean)
    .at(-1) ?? '';

export const getCanonicalFaqAnchorId = (anchor: string) => {
  const normalizedAnchor = getFaqAnchorId(getLastHashSegment(anchor));

  return FAQ_HASH_ALIASES[normalizedAnchor] ?? normalizedAnchor;
};

const getElementText = (element: Element) =>
  element.textContent?.replace(/\s+/g, ' ').trim() ?? '';

const setScrollOffset = (element: HTMLElement) => {
  element.style.scrollMarginTop = '96px';
};

export const addFaqSectionAnchors = (root: HTMLElement) => {
  const headings = root.querySelectorAll<HTMLElement>('h1, h2');

  headings.forEach(heading => {
    const anchorId = getFaqAnchorId(getElementText(heading));

    if (!FAQ_ANCHOR_IDS.has(anchorId)) {
      return;
    }

    heading.id = anchorId;
    heading.dataset.faqAnchor = anchorId;
    setScrollOffset(heading);
  });

  Object.entries(FAQ_ANCHOR_CONFIG).forEach(([anchorId, config]) => {
    if (!config.cmsContentId) {
      return;
    }

    const content = document.getElementById(config.cmsContentId);

    if (content instanceof HTMLElement && root.contains(content)) {
      content.dataset.faqAnchor = anchorId;
      setScrollOffset(content);
    }
  });
};

const getInputLabelText = (input: HTMLInputElement) =>
  Array.from(input.labels ?? [])
    .map(label => getElementText(label))
    .join(' ');

const markInputAsChecked = (input: HTMLInputElement) => {
  if (!input.checked) {
    input.checked = true;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

const elementMatchesAnchor = (element: Element, anchorId: string) => {
  const textAnchorId = getFaqAnchorId(getElementText(element));

  return textAnchorId === anchorId;
};

const inputMatchesAnchor = (input: HTMLInputElement, anchorId: string) => {
  const inputText = [
    input.id,
    input.name,
    input.value,
    input.getAttribute('aria-label') ?? '',
    getInputLabelText(input)
  ].join(' ');

  return getFaqAnchorId(inputText) === anchorId;
};

const activateInputForAnchor = (root: HTMLElement, anchorId: string) => {
  const inputs = root.querySelectorAll<HTMLInputElement>(
    'input[type="radio"], input[type="checkbox"]'
  );

  for (const input of Array.from(inputs)) {
    if (inputMatchesAnchor(input, anchorId)) {
      markInputAsChecked(input);
      return true;
    }
  }

  return false;
};

const activateConfiguredTabForAnchor = (root: HTMLElement, anchorId: string) => {
  const tabInputId = FAQ_ANCHOR_CONFIG[anchorId]?.tabInputId;

  if (!tabInputId) {
    return false;
  }

  const input = document.getElementById(tabInputId);

  if (!(input instanceof HTMLInputElement) || !root.contains(input)) {
    return false;
  }

  markInputAsChecked(input);

  return true;
};

const activateControlForAnchor = (root: HTMLElement, anchorId: string) => {
  const controls = root.querySelectorAll<HTMLElement>(
    'label, button, a[href^="#"], [role="button"], [role="tab"]'
  );

  for (const control of Array.from(controls)) {
    if (!elementMatchesAnchor(control, anchorId)) {
      continue;
    }

    if (control instanceof HTMLLabelElement && control.htmlFor) {
      const input = document.getElementById(control.htmlFor);

      if (input instanceof HTMLInputElement) {
        markInputAsChecked(input);
        return true;
      }
    }

    if (control instanceof HTMLLabelElement) {
      const nestedInput = control.querySelector<HTMLInputElement>(
        'input[type="radio"], input[type="checkbox"]'
      );

      if (nestedInput) {
        markInputAsChecked(nestedInput);
        return true;
      }
    }

    control.click();
    return true;
  }

  return false;
};

const openParentDetails = (target: HTMLElement, root: HTMLElement) => {
  let parent = target.parentElement;

  while (parent && root.contains(parent)) {
    if (parent instanceof HTMLDetailsElement) {
      parent.open = true;
    }

    parent = parent.parentElement;
  }
};

const getFaqTarget = (root: HTMLElement, anchorId: string) => {
  const target = document.getElementById(anchorId);

  if (target instanceof HTMLElement && root.contains(target)) {
    return target;
  }

  const cmsContentId = FAQ_ANCHOR_CONFIG[anchorId]?.cmsContentId;
  const cmsTarget = cmsContentId ? document.getElementById(cmsContentId) : null;

  if (cmsTarget instanceof HTMLElement && root.contains(cmsTarget)) {
    return cmsTarget;
  }

  return null;
};

export const openFaqAnchor = (root: HTMLElement, anchor: string) => {
  const anchorId = getCanonicalFaqAnchorId(anchor);
  const target = getFaqTarget(root, anchorId);

  if (!target) {
    return null;
  }

  openParentDetails(target, root);
  activateConfiguredTabForAnchor(root, anchorId) ||
    activateInputForAnchor(root, anchorId) ||
    activateControlForAnchor(root, anchorId);

  return target;
};

const isFaqPath = (pathname: string) => /^\/faq(?:\/l)?\/?$/.test(pathname);

const getFaqHashUrl = (anchorId: string) =>
  `${window.location.pathname}${window.location.search}#${anchorId}`;

export const getFaqAnchorIdFromHref = (
  href: string,
  currentHref: string
) => {
  const url = new URL(href, currentHref);
  const currentUrl = new URL(currentHref);

  if (
    url.origin !== currentUrl.origin ||
    !isFaqPath(url.pathname) ||
    !isFaqPath(currentUrl.pathname) ||
    !url.hash
  ) {
    return null;
  }

  return getCanonicalFaqAnchorId(decodeURIComponent(url.hash.replace(/^#/, '')));
};

const isPlainLeftClick = (event: MouseEvent) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

const getClickedLink = (event: MouseEvent) => {
  const link = (event.target as Element | null)?.closest('a[href]');

  return link instanceof HTMLAnchorElement ? link : null;
};

interface FaqAnchorsProps {
  rootRef: RefObject<HTMLElement | null>;
}

export const FaqAnchors = ({ rootRef }: FaqAnchorsProps) => {
  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    const openAnchor = (anchorId: string) => {
      if (!anchorId) {
        return;
      }

      const target = openFaqAnchor(root, anchorId);

      if (!target) {
        return;
      }

      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          target.scrollIntoView({ block: 'start' });
        }, 0);
      });
    };

    const openCurrentHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      const anchorId = getCanonicalFaqAnchorId(hash);

      if (hash !== anchorId) {
        window.history.replaceState(null, '', getFaqHashUrl(anchorId));
      }

      openAnchor(anchorId);
    };

    const handleFaqLinkClick = (event: MouseEvent) => {
      if (!isPlainLeftClick(event)) {
        return;
      }

      const link = getClickedLink(event);

      if (!link) {
        return;
      }

      const anchorId = getFaqAnchorIdFromHref(
        link.getAttribute('href') ?? '',
        window.location.href
      );

      if (!anchorId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      window.history.pushState(null, '', getFaqHashUrl(anchorId));
      openAnchor(anchorId);
    };

    addFaqSectionAnchors(root);
    openCurrentHash();

    window.addEventListener('hashchange', openCurrentHash);
    document.addEventListener('click', handleFaqLinkClick, true);

    return () => {
      window.removeEventListener('hashchange', openCurrentHash);
      document.removeEventListener('click', handleFaqLinkClick, true);
    };
  }, [rootRef]);

  return null;
};
